(() => {
  const rootPath = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const money = (cents, currency = 'MXN') => new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format((Number(cents) || 0) / 100);

  const initHeaderOverlays = (header) => {
    if (!header || header.dataset.overlaysReady) return;
    header.dataset.overlaysReady = 'true';

    const searchModal = header.querySelector('[data-tt-search-modal]');
    const cartDrawer = header.querySelector('[data-tt-cart-drawer]');
    const searchInput = header.querySelector('[data-tt-search-input]');
    const searchResults = header.querySelector('[data-tt-search-results]');
    const cartItems = header.querySelector('[data-tt-cart-items]');
    const cartSubtotal = header.querySelector('[data-tt-cart-subtotal]');
    const cartDrawerCount = header.querySelector('[data-tt-cart-drawer-count]');
    const discountForm = header.querySelector('[data-tt-discount-form]');
    const discountInput = header.querySelector('[data-tt-discount-input]');
    let searchTimer;
    let lastTrigger;

    const syncPageLock = () => {
      const open = searchModal?.classList.contains('is-open') || cartDrawer?.classList.contains('is-open');
      document.documentElement.classList.toggle('tt-header-overlay-open', Boolean(open));
    };

    const closePanel = (panel, triggerSelector) => {
      if (!panel || panel.hidden) return;
      panel.classList.remove('is-open');
      header.querySelectorAll(triggerSelector).forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      window.setTimeout(() => { if (!panel.classList.contains('is-open')) panel.hidden = true; }, 360);
      syncPageLock();
    };

    const openPanel = (panel, triggerSelector, trigger) => {
      if (!panel) return;
      lastTrigger = trigger;
      if (panel !== searchModal) closePanel(searchModal, '[data-tt-search-open]');
      if (panel !== cartDrawer) closePanel(cartDrawer, '[data-tt-cart-open]');
      header.classList.remove('is-open');
      document.documentElement.classList.remove('tt-mobile-menu-open');
      panel.hidden = false;
      window.requestAnimationFrame(() => panel.classList.add('is-open'));
      header.querySelectorAll(triggerSelector).forEach((item) => item.setAttribute('aria-expanded', 'true'));
      syncPageLock();
    };

    const closeSearch = () => { closePanel(searchModal, '[data-tt-search-open]'); lastTrigger?.focus(); };
    const closeCart = () => { closePanel(cartDrawer, '[data-tt-cart-open]'); lastTrigger?.focus(); };

    const renderSearch = (products) => {
      searchResults.replaceChildren();
      if (!products.length) {
        const empty = document.createElement('p');
        empty.textContent = 'No encontramos esa mezcla. Prueba con otro sabor.';
        searchResults.append(empty);
        return;
      }
      products.forEach((product) => {
        const link = document.createElement('a');
        link.className = 'ttf-search-result';
        link.href = product.url;
        const image = document.createElement('img');
        image.src = product.featured_image?.url || product.image || '';
        image.alt = '';
        image.loading = 'lazy';
        const copy = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = product.title;
        const type = document.createElement('small');
        type.textContent = product.type || 'Tea Town';
        copy.append(title, type);
        const arrow = document.createElement('span');
        arrow.textContent = '→';
        link.append(image, copy, arrow);
        link.addEventListener('click', closeSearch);
        searchResults.append(link);
      });
    };

    const runSearch = async (query) => {
      if (!query.trim()) {
        searchResults.innerHTML = '<p>Escribe para descubrir productos Tea Town.</p>';
        return;
      }
      searchResults.classList.add('is-loading');
      try {
        const response = await fetch(`${rootPath()}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=last`);
        if (!response.ok) throw new Error('Search request failed');
        const data = await response.json();
        renderSearch(data.resources?.results?.products || []);
      } catch (_) {
        searchResults.innerHTML = '<p>No pudimos cargar los resultados. Intenta de nuevo.</p>';
      } finally {
        searchResults.classList.remove('is-loading');
      }
    };

    const setCartCount = (count) => {
      header.querySelectorAll('[data-tt-cart-count]').forEach((badge) => {
        badge.textContent = count;
        badge.hidden = count < 1;
      });
      if (cartDrawerCount) cartDrawerCount.textContent = count;
    };

    const renderLegacyCart = (cart) => {
      setCartCount(cart.item_count || 0);
      cartSubtotal.textContent = money(cart.total_price, cart.currency || 'MXN');
      cartItems.replaceChildren();
      if (!cart.items?.length) {
        const empty = document.createElement('div');
        empty.className = 'ttf-cart-empty';
        empty.innerHTML = '<strong>Tu carrito esta listo para una gran mezcla.</strong><span>Agrega productos y construye tu siguiente pedido.</span>';
        cartItems.append(empty);
        return;
      }
      cart.items.forEach((item) => {
        const article = document.createElement('article');
        article.className = 'ttf-cart-item';
        const media = document.createElement('a');
        media.className = 'ttf-cart-item__image';
        media.href = item.url;
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = '';
          image.loading = 'lazy';
          media.append(image);
        }
        const copy = document.createElement('div');
        copy.className = 'ttf-cart-item__copy';
        const title = document.createElement('a');
        title.href = item.url;
        title.textContent = item.product_title;
        copy.append(title);
        if (item.variant_title && item.variant_title !== 'Default Title') {
          const variant = document.createElement('small');
          variant.textContent = item.variant_title;
          copy.append(variant);
        }
        const meta = document.createElement('div');
        meta.className = 'ttf-cart-item__meta';
        const quantity = document.createElement('div');
        quantity.className = 'ttf-cart-item__quantity';
        const minus = document.createElement('button');
        minus.type = 'button'; minus.textContent = '−'; minus.dataset.ttCartChange = item.key; minus.dataset.quantity = Math.max(0, item.quantity - 1); minus.setAttribute('aria-label', 'Reducir cantidad');
        const number = document.createElement('span');
        number.textContent = item.quantity;
        const plus = document.createElement('button');
        plus.type = 'button'; plus.textContent = '+'; plus.dataset.ttCartChange = item.key; plus.dataset.quantity = item.quantity + 1; plus.setAttribute('aria-label', 'Aumentar cantidad');
        quantity.append(minus, number, plus);
        const price = document.createElement('strong');
        price.textContent = money(item.final_line_price, cart.currency || 'MXN');
        meta.append(quantity, price);
        copy.append(meta);
        article.append(media, copy);
        cartItems.append(article);
      });
    };

    const renderCart = (cart) => {
      setCartCount(cart.item_count || 0);
      cartSubtotal.textContent = money(cart.total_price, cart.currency || 'MXN');
      cartItems.replaceChildren();
      const hasItems = Boolean(cart.items?.length);
      cartDrawer.classList.toggle('is-empty', !hasItems);
      if (!hasItems) {
        const empty = document.createElement('div');
        empty.className = 'ttf-cart-empty';
        empty.innerHTML = '<strong>Tu carrito est&aacute; vac&iacute;o</strong><p>&iquest;Tienes una cuenta? <a href="' + rootPath() + 'account/login">Inicia sesi&oacute;n</a> para pagar m&aacute;s r&aacute;pido.</p><a class="ttf-cart-empty__button" href="' + rootPath() + 'collections/all">Seguir comprando</a>';
        cartItems.append(empty);
        return;
      }
      cart.items.forEach((item) => {
        const article = document.createElement('article');
        article.className = 'ttf-cart-item';
        const media = document.createElement('a');
        media.className = 'ttf-cart-item__image';
        media.href = item.url;
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = item.product_title || '';
          image.loading = 'lazy';
          media.append(image);
        }
        const copy = document.createElement('div');
        copy.className = 'ttf-cart-item__copy';
        const heading = document.createElement('div');
        heading.className = 'ttf-cart-item__heading';
        const title = document.createElement('a');
        title.href = item.url;
        title.textContent = item.product_title;
        const linePrice = document.createElement('strong');
        linePrice.textContent = money(item.final_line_price, cart.currency || 'MXN');
        heading.append(title, linePrice);
        copy.append(heading);
        const details = (item.options_with_values || []).filter((option) => option.value && option.value !== 'Default Title' && option.name !== 'Title').map((option) => `${option.name}: ${option.value}`);
        if (!details.length && item.variant_title && item.variant_title !== 'Default Title') details.push(item.variant_title);
        if (details.length) {
          const variant = document.createElement('small');
          variant.textContent = details.join(' / ');
          copy.append(variant);
        }
        const unitPrice = document.createElement('span');
        unitPrice.className = 'ttf-cart-item__unit-price';
        unitPrice.textContent = money(item.final_price, cart.currency || 'MXN');
        copy.append(unitPrice);
        const meta = document.createElement('div');
        meta.className = 'ttf-cart-item__meta';
        const quantity = document.createElement('div');
        quantity.className = 'ttf-cart-item__quantity';
        const minus = document.createElement('button');
        minus.type = 'button';
        minus.textContent = '-';
        minus.dataset.ttCartChange = item.key;
        minus.dataset.quantity = Math.max(1, item.quantity - 1);
        minus.setAttribute('aria-label', 'Disminuir cantidad');
        minus.disabled = item.quantity <= 1;
        const number = document.createElement('span');
        number.textContent = item.quantity;
        const plus = document.createElement('button');
        plus.type = 'button';
        plus.textContent = '+';
        plus.dataset.ttCartChange = item.key;
        plus.dataset.quantity = item.quantity + 1;
        plus.setAttribute('aria-label', 'Aumentar cantidad');
        quantity.append(minus, number, plus);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'ttf-cart-item__remove';
        remove.dataset.ttCartChange = item.key;
        remove.dataset.quantity = 0;
        remove.setAttribute('aria-label', `Eliminar ${item.product_title}`);
        remove.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2m-9 0 1 14h8l1-14M10 10v6m4-6v6"/></svg>';
        meta.append(quantity, remove);
        copy.append(meta);
        article.append(media, copy);
        cartItems.append(article);
      });
    };

    const loadCart = async () => {
      try {
        const response = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Cart request failed');
        renderCart(await response.json());
      } catch (_) {
        cartItems.innerHTML = '<p class="ttf-cart-drawer__loading">No pudimos cargar el carrito. Intenta de nuevo.</p>';
      }
    };

    const changeCart = async (key, quantity, button) => {
      button.disabled = true;
      try {
        const response = await fetch(`${rootPath()}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: Number(quantity) }) });
        if (!response.ok) throw new Error('Cart update failed');
        renderCart(await response.json());
      } catch (_) {
        await loadCart();
      }
    };

    header.querySelectorAll('[data-tt-search-open]').forEach((trigger) => trigger.addEventListener('click', () => {
      openPanel(searchModal, '[data-tt-search-open]', trigger);
      window.setTimeout(() => searchInput?.focus(), 120);
    }));
    header.querySelectorAll('[data-tt-cart-open]').forEach((trigger) => trigger.addEventListener('click', () => {
      openPanel(cartDrawer, '[data-tt-cart-open]', trigger);
      loadCart();
    }));
    header.querySelectorAll('[data-tt-search-close]').forEach((button) => button.addEventListener('click', closeSearch));
    header.querySelectorAll('[data-tt-cart-close]').forEach((button) => button.addEventListener('click', closeCart));
    searchInput?.addEventListener('input', () => { window.clearTimeout(searchTimer); searchTimer = window.setTimeout(() => runSearch(searchInput.value), 220); });
    discountForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      const code = discountInput?.value.trim();
      if (!code) { discountInput?.focus(); return; }
      window.location.assign(`${rootPath()}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/checkout')}`);
    });
    cartItems?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-tt-cart-change]');
      if (button) changeCart(button.dataset.ttCartChange, button.dataset.quantity, button);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (searchModal?.classList.contains('is-open')) closeSearch();
      if (cartDrawer?.classList.contains('is-open')) closeCart();
    });
    document.addEventListener('cart:updated', loadCart);
    document.addEventListener('cart:update', loadCart);
  };

  const initProductCardForms = () => {
    if (document.documentElement.dataset.ttProductCardsReady) return;
    document.documentElement.dataset.ttProductCardsReady = 'true';
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest?.('.tea-town-product-card__form');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      const label = button?.querySelector('span');
      const originalLabel = label?.textContent || 'Agregar al carrito';
      if (button?.disabled) return;
      if (button) button.disabled = true;
      if (label) label.textContent = 'Agregando...';
      try {
        const response = await fetch(`${rootPath()}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error('Cart add failed');
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.querySelector('[data-tt-cart-open]')?.click();
        if (label) label.textContent = 'Agregado';
      } catch (_) {
        if (label) label.textContent = 'Intenta de nuevo';
      } finally {
        window.setTimeout(() => {
          if (button) button.disabled = false;
          if (label) label.textContent = originalLabel;
        }, 900);
      }
    });
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-tea-town-header]').forEach(initHeaderOverlays);
    initProductCardForms();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
